import { useQuery } from '@apollo/client'
import {
  createContext,
  Dispatch,
  FC,
  ReactElement,
  SetStateAction,
  useContext,
  useMemo,
  useState,
} from 'react'
import { GET_USERS } from './data/queries'

const ConversationContext = createContext<{
  users: UserType[]
  refetchUsers: () => void
  activeConversation: ConversationType | null
  setActiveConversation: Dispatch<SetStateAction<ConversationType | null>>
  activePersonId: number | null
  loading: boolean
}>({
  users: [],
  refetchUsers: () => {},
  activeConversation: null,
  setActiveConversation: () => {},
  activePersonId: null,
  loading: true,
})

export const useConversation = () => useContext(ConversationContext)

export const ConversationProvider: FC<{ children: ReactElement }> = ({
  children,
}) => {
  const [activeConversation, setActiveConversation] =
    useState<ConversationType | null>(null)
  const { data, loading, refetch } = useQuery(GET_USERS)

  // Apollo manages states internally, so we don't need to use useEffect with it
  const users: UserType[] = data?.users

  const values = useMemo(
    () => ({
      users,
      refetchUsers: refetch,
      activeConversation,
      setActiveConversation,
      activePersonId: activeConversation?.person?.id || null,
      loading,
    }),
    [users, loading, refetch, activeConversation, setActiveConversation],
  )

  return (
    <ConversationContext.Provider value={values}>
      {children}
    </ConversationContext.Provider>
  )
}
